import {
	PopupItemKind,
	PopupItemModel,
	getPopupQueue,
	markAsRead,
	postpone
} from "client/Api/PopupQueue/popupQueue";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback,
	useUserTimezone
} from "client/Hooks";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { never } from "client_legacy/ndaw/errors";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { HubDetailsDialogContent, HubDetailsDialogContentHandle } from "../Hub/HubDetailsDialogContent";
import {
	ActionListItem,
	Dialog,
	Toolbar
} from "client/Components";
import {
	ensureNotNil,
	isNotNil,
	resolveFormattedTextString
} from "client/Misc/utils";
import { DialogHeader } from "client/Components/Dialog/DialogHeader/DialogHeader";
import { Poller } from "client_legacy/ndaw/poller";
import { addDays, addTime } from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import { createRoot } from "react-dom/client";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { HubItemViewLocation } from "client/Api/Hub/common.details";
import { HttpRequestError } from "client/Api/httpClient";

export const PopupQueue = (): ReactElement | null => {
	const [popups, setPopups] = useState<readonly PopupItemModel[]>([]);

	const userTimezone: TimeZone = useUserTimezone();
	const alertManager: AlertManager = useAlerts();
	const dismissedPopupIdsRef: ReadonlyRefObject<Set<number>> = useRef<Set<number>>(new Set());

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetch(signal: AbortSignal): Promise<void> {
				try {
					const popups: readonly PopupItemModel[] = await getPopupQueue(signal);

					const popupsWithoutDismissed: PopupItemModel[] = popups.filter(
						p => !dismissedPopupIdsRef.current
							.has(p.id)
					);

					setPopups(popupsWithoutDismissed);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
				}
			}

			const poller = new Poller({
				callback: fetch,
				intervalInSeconds: userConfiguration()
					.popupQueueUpdateIntervalInSeconds
			});

			poller.start();

			return () => poller.stop();
		},
		[]
	);

	const closeHandler = useCallback(
		async (id: number): Promise<void> => {
			const dismissedPopupIds: Set<number> = dismissedPopupIdsRef.current;

			setPopups(popups => popups.filter(p => p.id !== id));
			dismissedPopupIds.add(id);

			try {
				await markAsRead(id);
			} catch(e) {
				alertManager.showError(localize("_GeneralErrorMessage"));
			} finally {
				dismissedPopupIds.delete(id);
			}
		},
		[alertManager]
	);

	const postponeHandler = useCallback(
		async (until: Date): Promise<void> => {
			const popupIds: number[] = popups.map(p => p.id);
			const dismissedPopups: Set<number> = dismissedPopupIdsRef.current;

			for (const popupId of popupIds) {
				dismissedPopups.add(popupId);
			}

			setPopups(
				popups => popups.filter(
					p => !dismissedPopups.has(p.id)
				)
			);

			try {
				await postpone({
					ids: popupIds,
					postponeUntil: until
				});
			} catch(e) {
				alertManager.showError(localize("_GeneralErrorMessage"));
			} finally {
				for (const popupId of popupIds) {
					dismissedPopups.delete(popupId);
				}
			}
		},
		[alertManager, popups]
	);

	if (popups.length === 0) {
		return null;
	}

	return (
		<PopupItemDialog
			model={popups[0]}
			allPopups={popups}
			onClose={closeHandler}
			onPostpone={postponeHandler}
			timezone={userTimezone}
		/>
	);
};
PopupQueue.displayName = "PopupQueue";

export type PopupItemDialogProps = {
	model: PopupItemModel;
	allPopups: readonly PopupItemModel[];
	onClose(id: number): void;
	onPostpone(until: Date): void;
	timezone: TimeZone;
};

export const PopupItemDialog = (props: PopupItemDialogProps): ReactElement => {
	const {
		model,
		allPopups,
		onClose: onCloseFromProps,
		onPostpone,
		timezone
	} = props;

	const onClose: (id: number) => void = useStableImperativeCallback(onCloseFromProps);

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const hubDetailsDialogContentRef: RefObject<HubDetailsDialogContentHandle | null> = useRef<HubDetailsDialogContentHandle>(null);

	const titleLocalizationPair = useMemo(
		(): LocalizationPair => {
			const uniqueKinds = new Set(allPopups.map(p => p.kind));

			if (uniqueKinds.size !== 1) {
				return variousKindsTitleLocalizationPair;
			}

			const kind: PopupItemKind = [...uniqueKinds][0];

			return itemKindToTitleLocalizationPair[kind];
		},
		[allPopups]
	);

	const resolveLocalizedValue = useCallback(
		(
			localization: LocalizationPair,
			placeholder: string
		): string => {
			const localizationKey: LocalizationKey = allPopups.length === 1
				? localization.singularFormKey
				: localization.multipleFormKey;

			return resolveFormattedTextString(
				{
					template: localize(localizationKey),
					replacements: [
						{
							what: placeholder,
							filler: `${allPopups.length}`
						}
					]
				}
			);
		},
		[allPopups.length]
	);

	const closeHandler = useCallback(
		async (): Promise<void> => {
			if (isNotNil(hubDetailsDialogContentRef.current)) {
				const shouldStay: boolean = await hubDetailsDialogContentRef.current
					.beforeCloseHandler();

				if (shouldStay) {
					return;
				}
			}

			onClose(model.id);
		},
		[model.id, onClose]
	);

	const postponeHandler = (by: "1hour" | "3hours" | "1day"): void => {
		const now = new Date();

		let until: Date;

		switch (by) {
			case "1hour":
				until = addTime(now, 1, "hour");
				break;
			case "3hours":
				until = addTime(now, 3, "hour");
				break;
			case "1day":
				until = addDays(now, 1, timezone);
				break;
			default:
				never(by);
		}

		onPostpone(until);
	};

	const loadingErrorHandler = useCallback(
		(error: unknown): TryResult<void> => {
			if (error instanceof HttpRequestError) {
				switch (error.statusCode) {
					case 403:
					case 404:
						// We explicitly bypass the "stay on page" functionality
						onClose(model.id);

						return {
							wasSuccessful: true,
							result: undefined
						};
					default:
						break;
				}
			}

			return {
				wasSuccessful: false
			};
		},
		[model.id, onClose]
	);

	const renderContent = (): ReactElement => {
		switch (model.kind) {
			case PopupItemKind.Announcement:
				return (
					<HubDetailsDialogContent
						id={model.announcementId}
						itemKind={HubItemKind.Announcement}
						onIsBusyChange={setIsBusy}
						ref={hubDetailsDialogContentRef}
						viewLocation={HubItemViewLocation.PopupQueue}
						onLoadingError={loadingErrorHandler}
					/>
				);
			case PopupItemKind.Event:
				return (
					<HubDetailsDialogContent
						id={model.eventId}
						itemKind={HubItemKind.Event}
						onIsBusyChange={setIsBusy}
						ref={hubDetailsDialogContentRef}
						viewLocation={HubItemViewLocation.PopupQueue}
						onLoadingError={loadingErrorHandler}
					/>
				);
			default:
				never(model);
		}
	};

	return (
		<Dialog
			trackingId="automatic-popup-dialog"
			hasBusyIndicator={isBusy}
			variant="left-slider"
			onClose={closeHandler}
		>
			<DialogHeader
				hasBorder
				title={resolveLocalizedValue(titleLocalizationPair, "{{N}}")}
				onClose={closeHandler}
			/>
			<Dialog.Body goesEdgeToEdge>
				{renderContent()}
			</Dialog.Body>
			<Dialog.Footer hasBorder>
				<Toolbar>
					<Toolbar.MenuButton
						trackingId="postpone"
						text={resolveLocalizedValue(postponeButtonLocalizationPair, "{{N}}")}
						kind="secondary"
						rightIcon="chevron-down"
					>
						<ActionListItem
							text={localize("_In1Hour")}
							onClick={() => postponeHandler("1hour")}
						/>
						<ActionListItem
							text={resolveFormattedTextString({
								template: localize("_InNHours"),
								replacements: [
									{
										what: "{{N}}",
										filler: "3"
									}
								]
							})}
							onClick={() => postponeHandler("3hours")}
						/>
						<ActionListItem
							text={localize("_In1Day")}
							onClick={() => postponeHandler("1day")}
						/>
					</Toolbar.MenuButton>
					<Toolbar.Button
						trackingId="close"
						text={localize(
							allPopups.length === 1
								? "_Close"
								: "_Next"
						)}
						onClick={closeHandler}
						kind="primary"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
PopupItemDialog.displayName = "PopupItemDialog";

export function init(): void {
	createRoot(
		ensureNotNil(
			document.getElementById("popup-queue-host")
		)
	)
		.render(
			<PopupQueue />
		);
}

const itemKindToTitleLocalizationPair: { [key in PopupItemKind]: LocalizationPair } = {
	[PopupItemKind.Announcement]: {
		singularFormKey: "_1NewAnnouncement",
		multipleFormKey: "_NNewAnnouncements"
	},
	[PopupItemKind.Event]: {
		singularFormKey: "_1NewEvent",
		multipleFormKey: "_NNewEvents"
	}
};

const variousKindsTitleLocalizationPair: LocalizationPair = {
	singularFormKey: "_1NewItem",
	multipleFormKey: "_NNewItems"
};

const postponeButtonLocalizationPair: LocalizationPair = {
	singularFormKey: "_ShowItem1Later",
	multipleFormKey: "_ShowItemsNLater"
};

type LocalizationPair = {
	singularFormKey: LocalizationKey;
	multipleFormKey: LocalizationKey;
};
